import { Button, MessageBar, MessageBarBody, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  CalendarLtr16Regular,
  CheckmarkCircle16Regular,
  Mail16Regular,
  Open16Regular,
  Phone16Regular,
} from '@fluentui/react-icons';
import type { TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  GridLayout,
  GridToolbarItems,
  MainGrid,
  NavigateNewRecordGridButton,
  NavigateOpenRecordGridButton,
} from '@powerportalspro/react-fluent';
import { useMemo, type ReactNode } from 'react';
import {
  ALL_CONTACTS_VIEW_ID,
  MY_CONTACTS_VIEW_ID,
  getAllContactsView,
  getMyContactsView,
  useCurrentContactId,
} from './AccountAndContactCustomViews';

// The contacts as cards the page lays out itself (`cardRenderer`): the photo and name at the top
// with the company beneath and an age badge beside, the email, phone and birthday each behind an
// icon, and buttons that open the contact and select its card.
//
// `card.field` renders a column as its table cell would — its formatting, its link and the
// search's highlighting — so the renderer only decides where each column goes. `card.avatar` is
// the default card's photo or initials, and `card.label` each column's header, which the details
// show as their tooltips. The grid still draws the card around what the renderer returns: its
// border, its selector, its selected state.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.CardLayout.CustomCardRenderingMainDemo';

const CONTACT_URL = 'demos/contact';

// The details under the name, each behind its icon; one the contact has no value for is left out.
const DETAILS: readonly { column: string; icon: ReactNode }[] = [
  { column: 'emailaddress1', icon: <Mail16Regular /> },
  { column: 'mobilephone', icon: <Phone16Regular /> },
  { column: 'birthdate', icon: <CalendarLtr16Regular /> },
];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  name: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  company: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  age: {
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusXLarge,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
  },
  younger: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
    color: tokens.colorStatusSuccessForeground1,
  },
  middle: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    color: tokens.colorStatusWarningForeground1,
  },
  older: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  detail: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    color: tokens.colorNeutralForeground2,
  },
  value: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
});

// The raw number behind a column, for the badge's color.
function readNumber(record: TableRecord, column: string): number | null {
  const cell = record.properties?.[column];
  const value = cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
  return typeof value === 'number' ? value : null;
}

export function CustomCardRenderingMainSample() {
  const styles = useStyles();
  const t = useT();
  // A contact's id, for views over contacts: an internal user has none, and sees no "my contacts".
  const currentContactId = useCurrentContactId();

  const customViews = useMemo(
    () => [getAllContactsView(), getMyContactsView(currentContactId)],
    [currentContactId],
  );

  return (
    <div className={styles.root}>
      <MessageBar intent="warning" layout="multiline">
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        defaultLayout={GridLayout.Cards}
        allowChangingLayout
        cardImageColumn="entityimage"
        minCardWidth="300px"
        minHeight="600px"
        maxHeight="600px"
        pageSize={20}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        customViewDefinitions={customViews}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
        cardRenderer={(card) => {
          // The badge's color comes from the raw age; its text is the formatted cell.
          const age = readNumber(card.record, 'ppp_age');
          const ageClass = age === null ? undefined : age > 65 ? styles.older : age > 40 ? styles.middle : styles.younger;
          return (
            <div className={styles.card}>
              <div className={styles.header}>
                {card.avatar}
                <div className={styles.identity}>
                  <span className={styles.name}>{card.field('fullname')}</span>
                  <span className={styles.company}>{card.field('parentcustomerid')}</span>
                </div>
                {ageClass && (
                  <span className={mergeClasses(styles.age, ageClass)} data-ppp-tooltip={card.label('ppp_age')}>
                    {card.field('ppp_age')}
                  </span>
                )}
              </div>

              <div className={styles.details}>
                {DETAILS.filter(({ column }) => card.displayValue(column)).map(({ column, icon }) => (
                  <div key={column} className={styles.detail} data-ppp-tooltip={card.label(column)}>
                    {icon}
                    <span className={styles.value}>{card.field(column)}</span>
                  </div>
                ))}
              </div>

              <div className={styles.actions}>
                <Button size="small" appearance="subtle" icon={<Open16Regular />} disabled={!card.canOpen} onClick={card.open}>
                  {t(`${STRINGS_BASE}.open`)}
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<CheckmarkCircle16Regular />}
                  onClick={() => card.setSelected(!card.isSelected)}
                >
                  {t(`${STRINGS_BASE}.${card.isSelected ? 'deselect' : 'select'}`)}
                </Button>
              </div>
            </div>
          );
        }}
      >
        <GridToolbarItems>
          <NavigateNewRecordGridButton url={CONTACT_URL} />
          <NavigateOpenRecordGridButton urlFor={(record) => `${CONTACT_URL}?contactId=${record.id}`} />
        </GridToolbarItems>
      </MainGrid>
    </div>
  );
}
